import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, Input } from 'antd';
import { CheckOutlined, CloseOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import styles from './MissingDetails.module.scss';

const MIN_PASSWORD_LENGTH = 1;
const nameSpace = 'uploadFiles.policyStep.missingDetailsSection';

interface MissingPasswordInputProps {
    maskedPassword?: string | null;
    onSave: (password: string) => Promise<void>;
    isLoading: boolean;
    hintText?: string;
    alwaysShowInput?: boolean;
}

const MissingPasswordInput: FC<MissingPasswordInputProps> = ({
    maskedPassword,
    onSave,
    isLoading,
    hintText,
    alwaysShowInput = false,
}) => {
    const { t } = useTranslation();
    const [password, setPassword] = useState<string>('');
    const [isSaved, setIsSaved] = useState(false);
    const [savedPassword, setSavedPassword] = useState<string>('');
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const hasSyncedRef = useRef(false);

    useEffect(() => {
        if (alwaysShowInput && maskedPassword && !hasSyncedRef.current) {
            setPassword(maskedPassword);
            hasSyncedRef.current = true;
        }
    }, [alwaysShowInput, maskedPassword]);

    const hasExistingPassword = !!maskedPassword;

    const displayPassword = isSaved ? savedPassword : (hasExistingPassword ? maskedPassword : null);

    if (displayPassword && !isEditing && !alwaysShowInput) {
        return (
            <div
                className={styles['password-chip']}
                data-testid="uploadDrawer_passwordChip"
                onClick={() => setIsEditing(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') setIsEditing(true); }}
            >
                <Button
                    type="link"
                    className={styles['password-chip-toggle']}
                    data-testid="uploadDrawer_passwordChipToggle"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsPasswordVisible(!isPasswordVisible);
                    }}
                >
                    {isPasswordVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </Button>
                <span className={styles['password-chip-value']} data-testid="uploadDrawer_passwordChipValue">
                    {isPasswordVisible ? displayPassword : '••••••••••••••••••••'}
                </span>
            </div>
        );
    }

    const isValid = password.length >= MIN_PASSWORD_LENGTH;

    const handleSubmit = async (): Promise<void> => {
        if (isValid) {
            await onSave(password);
            setSavedPassword(password);
            setIsSaved(true);
            setIsEditing(false);
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' && isValid) {
            handleSubmit();
        }
    };

    const handleClear = (): void => {
        if (displayPassword) {
            // Cancel editing, go back to view
            setPassword('');
            setIsEditing(false);
        } else {
            setPassword('');
        }
    };

    return (
        <div>
            <div className={styles['password-input-row']}>
                <Input.Password
                    className={styles['password-input']}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyUp={handleKeyUp}
                    placeholder={t(`${nameSpace}.enterPassword`)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    name="recipient-secret"
                />
                <div className={styles['action-buttons']}>
                    <Button
                        type="link"
                        className={styles['submit-btn']}
                        onClick={handleSubmit}
                        disabled={!isValid || isLoading}
                    >
                        <CheckOutlined />
                    </Button>
                    <Button
                        type="link"
                        className={styles['cancel-btn']}
                        onClick={handleClear}
                        disabled={isLoading || (!password && !displayPassword)}
                    >
                        <CloseOutlined />
                    </Button>
                </div>
            </div>
            {hintText && (
                <span className={styles['social-id-hint']}>{hintText}</span>
            )}
        </div>
    );
};

export default MissingPasswordInput;
